/**
 * The portal's categorical mark palette - one set for every small coloured
 * mark in the app: status dots, priority chevrons, pipeline-stage rails,
 * file-type icons and chat category badges.
 *
 * These are deliberately NOT theme tokens. Each is read as a member of one
 * categorical set, so a given meaning must keep the same identity whether the
 * app is light or dark. Every step below clears 3:1 against both card
 * surfaces (#ffffff light, #151d2e dark), so a single value serves both.
 *
 * The reserved semantic tokens (--color-success / --color-warning /
 * --color-danger) do flip per theme and stay reserved for actual state -
 * success toasts, destructive buttons, error text. Don't cross the two sets.
 *
 * Canonical scheme notes live in the demo repo's DESIGN-TOKENS.md.
 *
 * Consumers concatenate hex alpha onto these (`${hue}1a` for a 10% wash), so
 * every value must stay a 6-digit hex.
 */
export const STATUS_HUE = {
  /** Inert / unstarted / archived. Achromatic on purpose - absence of state. */
  neutral: "#94a3b8",
  /** Queued, assigned, not yet moving. */
  blue: "#5a8bd6",
  /** In flight, needs attention, mid-priority. */
  amber: "#d9a514",
  /** Under review - the one step that is neither progress nor a problem. */
  purple: "#a855f7",
  /** Complete, live, won. */
  green: "#1f9d57",
  /** Failed, lost, urgent. */
  red: "#e5493a",
  /** High priority - sits between amber and red so the ramp reads as a ramp. */
  orange: "#f0892a",
} as const;

export type StatusHue = keyof typeof STATUS_HUE;
